import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Anchor, ShieldCheck, FileSignature, Stamp, Clock, Eye, EyeOff, ExternalLink, AlertTriangle, Copy, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AboutDialog from "@/components/AboutDialog";

function truncateHash(h, head = 14, tail = 8) {
  if (!h || h.length <= head + tail + 3) return h;
  return `${h.slice(0, head)}…${h.slice(-tail)}`;
}

export default function PublicReceipt() {
  const { hash } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.publicReceipt(hash).then(setReceipt).catch((e) => {
      setError(e?.response?.status === 404 ? "Receipt not found." : "Could not load receipt.");
    });
  }, [hash]);

  const copyHash = async () => {
    if (!receipt?.receipt_hash) return;
    try {
      await navigator.clipboard.writeText(receipt.receipt_hash);
      setCopied(true);
      toast.success("Receipt hash copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  if (error) {
    return (
      <div className="max-w-[700px] mx-auto px-6 py-20 text-center">
        <AlertTriangle size={28} className="mx-auto mb-4 text-[var(--ink-soft)]"/>
        <h1 className="display text-[24px]">{error}</h1>
        <p className="text-[var(--ink-soft)] mt-2">The receipt hash may be incorrect or revoked.</p>
        <Link to="/" className="text-[var(--navy)] fc-link-underline text-[13px] mt-4 inline-block">Back to FreightCierge</Link>
      </div>
    );
  }
  if (!receipt) return <div className="max-w-[900px] mx-auto px-6 py-12 text-[var(--ink-soft)]">Loading receipt…</div>;

  const meta = receipt.action_outcome === "approved"
    ? { Icon: Stamp, chip: "fc-chip-approved", label: "Approved · KATLAS-signed" }
    : { Icon: Clock, chip: "fc-chip-escalated", label: "Refused / on hold · KATLAS-signed" };
  const Icon = meta.Icon;

  return (
    <div className="bg-[var(--paper)] min-h-screen pb-16 fc-fade-up" data-testid="public-receipt-root">
      {/* rec-06 · Sticky trust band — KATLAS Verified + node timestamp + truncated hash + copy */}
      <div className="bg-[var(--navy)] text-white sticky top-0 z-30" data-testid="public-trust-band">
        <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-white text-[var(--green)] text-[11px] mono font-semibold uppercase tracking-[0.08em]" data-testid="verified-pill">
            <ShieldCheck size={12} strokeWidth={2.2}/> KATLAS verified
          </span>
          <span className="text-white/60 text-[11px] hidden sm:inline">·</span>
          <span className="mono text-[11px] text-white/85" data-testid="public-node-ts" title={receipt.node_timestamp}>
            {receipt.node_timestamp ? new Date(receipt.node_timestamp).toUTCString() : "—"}
          </span>
          <span className="text-white/60 text-[11px] hidden sm:inline">·</span>
          <button
            onClick={copyHash}
            className="inline-flex items-center gap-1.5 mono text-[11px] text-white/90 hover:text-white"
            data-testid="public-trust-hash"
            title={receipt.receipt_hash}
          >
            {truncateHash(receipt.receipt_hash)}
            {copied ? <Check size={11}/> : <Copy size={11}/>}
          </button>
          <div className="ml-auto flex items-center gap-3">
            <AboutDialog variant="light" testid="public-about-trigger"/>
            <Link to="/" className="text-white/70 hover:text-white text-[11px]">FreightCierge ↗</Link>
          </div>
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-6 pt-10">
        <div className="flex items-center gap-2 mb-2">
          <Anchor size={14} className="text-[var(--navy)]"/>
          <span className="fc-eyebrow">FreightCierge · KATLAS-signed receipt</span>
        </div>
        <h1 className="display text-[32px] leading-tight mb-1" data-testid="public-action-taken">{receipt.action_taken}</h1>
        <div className="flex items-center gap-3 mt-2 mb-6">
          <span className={`fc-chip ${meta.chip}`} data-testid="public-status-chip"><Icon size={11}/> {meta.label}</span>
          <span className="inline-flex items-center gap-1.5 text-[12px] text-[var(--ink-soft)]">
            <ShieldCheck size={12} className="text-[var(--green)]"/>
            verification: <span className="mono text-[var(--ink)]">{receipt.verification_status || "—"}</span>
          </span>
        </div>

        <div className="fc-card p-5 grid sm:grid-cols-2 gap-5" data-testid="public-receipt-card">
          <Kv label="Receipt hash"      value={<span className="mono break-all" data-testid="public-receipt-hash">{receipt.receipt_hash}</span>}/>
          <Kv label="Attestation ref"   value={<span className="mono break-all">{receipt.attestation_ref}</span>}/>
          <Kv label="Signer"            value={<span className="mono break-all">{receipt.signer_ref || `${receipt.signer_issuer || "—"}#${receipt.signer_key_id || "—"}`}</span>}/>
          {receipt.signer_fingerprint && <Kv label="Signer fingerprint" value={<span className="mono break-all">{receipt.signer_fingerprint}</span>}/>}
          <Kv label="Node timestamp"    value={<span className="mono">{receipt.node_timestamp}</span>}/>
          <Kv label="Verification"      value={<span className="mono">{receipt.verification || "—"}</span>}/>
          <Kv label="Policy gate"       value={<span className="mono">{receipt.policy_gate}</span>}/>
          <Kv label="Outcome"           value={<span className="mono">{receipt.action_outcome}</span>}/>
        </div>

        <section className="mt-8" data-testid="public-shared-evidence">
          <div className="fc-eyebrow mb-3">Shared evidence references</div>
          <div className="border border-[var(--line)] bg-white divide-y divide-[var(--line)]">
            {(receipt.shared_evidence_refs || []).map((e, i) => (
              <div key={i} className="px-4 py-2.5 flex justify-between text-[12px] gap-3">
                <span className="mono">{e.label}</span>
                <span className="mono text-[var(--ink-soft)] truncate">{e.ref}</span>
              </div>
            ))}
          </div>
          <div className="text-[12px] mt-3">
            <span className="mono text-[var(--ink-soft)]">Withheld evidence (not sent to KATLAS):</span>{" "}
            <span className="mono">{receipt.withheld_evidence_count}</span>
            <span className="text-[var(--ink-soft)]"> · withheld asset bodies are not exposed publicly.</span>
          </div>
        </section>

        <section className="mt-8" data-testid="public-car">
          <div className="fc-eyebrow mb-3">CAR · Custody · Authority · Receipts</div>
          <div className="grid sm:grid-cols-2 gap-3 text-[12px]">
            <div className="border border-[var(--line)] bg-white p-3">
              <div className="mono uppercase text-[10px] tracking-wider text-[var(--ink-soft)]">Custody holder</div>
              <div className="mono mt-1">{receipt.car?.custody_holder}</div>
            </div>
            <div className="border border-[var(--line)] bg-white p-3">
              <div className="mono uppercase text-[10px] tracking-wider text-[var(--ink-soft)]">Authority holder</div>
              <div className="mono mt-1">{receipt.car?.authority_holder}</div>
            </div>
          </div>
          <div className="text-[12px] mt-3 italic text-[var(--ink-soft)]">{receipt.car_line || receipt.car?.line}</div>
        </section>

        <section className="mt-8 border-l-2 border-[var(--navy)] pl-4 py-2" data-testid="public-data-boundary">
          <FileSignature size={14} className="text-[var(--navy)] mb-1"/>
          <p className="text-[13px]">{receipt.data_boundary}</p>
        </section>

        <div className="mt-10 text-[11px] text-[var(--ink-soft)] mono">
          This public-safe view does not expose bearer tokens, the Authorization header, cert text or the signed receipt envelope.
        </div>
      </div>
    </div>
  );
}

function Kv({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="fc-eyebrow mb-1">{label}</div>
      <div className="text-[13px] min-w-0">{value || <span className="text-[var(--ink-soft)]">—</span>}</div>
    </div>
  );
}
